import { LitElement, html, type PropertyValues } from 'lit';
import { toCSSValue } from '@loadsmart/miranda-tokens';

import type { SwitcherProps } from './switcher.types';
import { defaultSwitcherProps } from './switcher.constants';
import { switcherStyles } from './switcher.styles';

export class Switcher extends LitElement {
	static override styles = [switcherStyles()];

	static override get properties() {
		return {
			gap: { type: String },
			rowGap: { type: String, attribute: 'row-gap' },
			columnGap: { type: String, attribute: 'column-gap' },
			threshold: { type: String },
			limit: { type: Number },
		};
	}

	declare gap: SwitcherProps['gap'];

	declare rowGap: SwitcherProps['rowGap'];

	declare columnGap: SwitcherProps['columnGap'];

	declare threshold: SwitcherProps['threshold'];

	declare limit: SwitcherProps['limit'];

	constructor() {
		super();

		this.gap = defaultSwitcherProps.gap;
		this.threshold = defaultSwitcherProps.threshold;
	}

	protected override update(
		changedProperties: PropertyValues<SwitcherProps>,
	): void {
		super.update(changedProperties);

		if (changedProperties.has('gap')) {
			this.style.setProperty(
				'--m-switcher-gap',
				(this.gap && toCSSValue(this.gap)) ??
					toCSSValue(defaultSwitcherProps.gap),
			);
		}
		if (changedProperties.has('rowGap')) {
			if (this.rowGap) {
				this.style.setProperty('--m-switcher-row-gap', toCSSValue(this.rowGap));
			} else {
				this.style.removeProperty('--m-switcher-row-gap');
			}
		}
		if (changedProperties.has('columnGap')) {
			if (this.columnGap) {
				this.style.setProperty(
					'--m-switcher-column-gap',
					toCSSValue(this.columnGap),
				);
			} else {
				this.style.removeProperty('--m-switcher-column-gap');
			}
		}
		if (changedProperties.has('threshold')) {
			this.style.setProperty(
				'--m-switcher-threshold',
				this.threshold || defaultSwitcherProps.threshold,
			);
		}
	}

	override render() {
		const limitStyles = this.limit
			? html`
					<style>
						::slotted(*:nth-last-child(n + ${this.limit + 1})) {
							flex-basis: 100%;
						}
						::slotted(*:nth-last-child(n + ${this.limit + 1}) ~ *) {
							flex-basis: 100%;
						}
					</style>
			  `
			: '';

		return html`
			${limitStyles}
			<slot></slot>
		`;
	}
}

window.customElements.define('m-switcher', Switcher);

declare global {
	interface HTMLElementTagNameMap {
		'm-switcher': Switcher;
	}
}
